import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Building2,
  CheckCircle,
  Clock,
  Eye,
  FileText,
  Filter,
  Pencil,
  Plus,
  X,
} from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { FormField, selectClass } from "../components/FormField";
import { conventionStatusOptions } from "../data";
import { useAdminConventions } from "../hooks/useAdminConventions";
import { cn } from "@/shared/lib/cn";

function formatBudget(value: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(value);
}

function statusBadgeClass(status: string) {
  switch (status) {
    case "active":
      return "bg-teal-soft text-green";
    case "expiring":
      return "bg-yellow-soft text-yellow";
    case "expired":
    case "cancelled":
      return "bg-pink-soft text-pink";
    case "suspended":
      return "bg-card-muted text-muted-foreground";
    default:
      return "bg-primary-soft text-primary";
  }
}

export function ConventionsPage() {
  const { t } = useTranslation();
  const labels = t.safecheck.conventions;
  const navigate = useNavigate();
  const {
    conventions,
    loading,
    error,
    usingApi,
    companyFilterOptions,
    providerFilterOptions,
  } = useAdminConventions();

  const [draftStatus, setDraftStatus] = useState("");
  const [draftCompany, setDraftCompany] = useState("");
  const [draftProvider, setDraftProvider] = useState("");
  const [appliedStatus, setAppliedStatus] = useState("");
  const [appliedCompany, setAppliedCompany] = useState("");
  const [appliedProvider, setAppliedProvider] = useState("");

  const summary = useMemo(
    () => ({
      total: conventions.length,
      active: conventions.filter((item) => item.status === "active").length,
      expiringSoon: conventions.filter((item) => item.status === "expiring").length,
      budgetAlerts: conventions.filter((item) => item.consumptionPct >= 80).length,
    }),
    [conventions],
  );

  const applyFilters = () => {
    setAppliedStatus(draftStatus);
    setAppliedCompany(draftCompany);
    setAppliedProvider(draftProvider);
  };

  const resetFilters = () => {
    setDraftStatus("");
    setDraftCompany("");
    setDraftProvider("");
    setAppliedStatus("");
    setAppliedCompany("");
    setAppliedProvider("");
  };

  const filtered = useMemo(() => {
    return conventions.filter((convention) => {
      const byStatus = appliedStatus ? convention.status === appliedStatus : true;
      const byCompany = appliedCompany
        ? convention.companyId === appliedCompany || convention.company === appliedCompany
        : true;
      const byProvider = appliedProvider
        ? (convention.providerIds ?? []).includes(appliedProvider)
        : true;
      return byStatus && byCompany && byProvider;
    });
  }, [conventions, appliedStatus, appliedCompany, appliedProvider]);

  const summaryCards = [
    {
      label: labels.summary.total,
      value: summary.total,
      valueClass: "text-foreground",
      icon: FileText,
      color: "text-purple",
      bg: "bg-purple-soft",
    },
    {
      label: labels.summary.active,
      value: summary.active,
      valueClass: "text-green",
      icon: CheckCircle,
      color: "text-green",
      bg: "bg-teal-soft",
    },
    {
      label: labels.summary.expiringSoon,
      value: summary.expiringSoon,
      valueClass: "text-yellow",
      icon: Clock,
      color: "text-yellow",
      bg: "bg-yellow-soft",
    },
    {
      label: labels.summary.budgetAlerts,
      value: summary.budgetAlerts,
      valueClass: "text-pink",
      icon: AlertTriangle,
      color: "text-pink",
      bg: "bg-pink-soft",
    },
  ];

  return (
    <>
      <Breadcrumbs items={[{ label: labels.title }]} />

      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-foreground">{labels.title}</h1>
          {usingApi && (
            <p className="mt-1 text-xs text-muted">
              {loading ? "Chargement…" : `${conventions.length} convention(s) API`}
            </p>
          )}
          {error && <p className="mt-1 text-xs text-pink">{error}</p>}
        </div>
        <Link
          to="/dashboard/conventions/create"
          className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary/90"
        >
          <Plus size={16} />
          {labels.add}
        </Link>
      </div>

      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {summaryCards.map((card) => (
          <div key={card.label} className="theme-card theme-card-interactive p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted">{card.label}</p>
                <p className={cn("mt-1 text-2xl font-bold", card.valueClass)}>{card.value}</p>
              </div>
              <div className={cn("flex h-10 w-10 items-center justify-center rounded-full", card.bg)}>
                <card.icon size={20} className={card.color} />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="theme-card mb-6 p-5">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <FormField label={labels.fields.status}>
            <select
              value={draftStatus}
              onChange={(event) => setDraftStatus(event.target.value)}
              className={selectClass}
            >
              {conventionStatusOptions.map((option) => (
                <option key={option.value || "all"} value={option.value}>
                  {option.value ? option.label : labels.fields.allStatuses}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label={labels.fields.company}>
            <select
              value={draftCompany}
              onChange={(event) => setDraftCompany(event.target.value)}
              className={selectClass}
            >
              {companyFilterOptions.map((option) => (
                <option key={option.value || "all"} value={option.value}>
                  {option.value ? option.label : labels.fields.allCompanies}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label={labels.fields.provider}>
            <select
              value={draftProvider}
              onChange={(event) => setDraftProvider(event.target.value)}
              className={selectClass}
            >
              {providerFilterOptions.map((option) => (
                <option key={option.value || "all"} value={option.value}>
                  {option.value ? option.label : labels.fields.allProviders}
                </option>
              ))}
            </select>
          </FormField>
        </div>
        <div className="mt-4 flex items-center gap-2">
          <button
            type="button"
            onClick={applyFilters}
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-card-muted px-4 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-border/60 md:flex-none"
          >
            <Filter size={16} />
            {t.safecheck.filter}
          </button>
          <button
            type="button"
            onClick={resetFilters}
            className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-border bg-card text-muted-foreground transition-colors hover:bg-card-muted"
            aria-label={t.safecheck.reset}
          >
            <X size={16} />
          </button>
        </div>
      </div>

      <div className="theme-card">
        <div className="border-b border-border px-5 py-4">
          <h2 className="text-sm font-semibold text-foreground">
            {labels.listTitle} ({filtered.length})
          </h2>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[1200px]">
            <thead>
              <tr className="border-b border-border bg-card-muted/50">
                {Object.values(labels.columns).map((header) => (
                  <th
                    key={header}
                    className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-muted"
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((convention) => (
                <tr
                  key={convention.id}
                  className="border-b border-border last:border-0 hover:bg-card-muted/30"
                >
                  <td className="px-4 py-3 text-sm font-medium text-primary">{convention.code}</td>
                  <td className="px-4 py-3 text-sm font-semibold text-foreground">{convention.name}</td>
                  <td className="px-4 py-3 text-sm text-foreground">{convention.company}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1.5 rounded-md bg-primary-soft px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-primary">
                      <Building2 size={12} />
                      {labels.providersBadge.replace("{count}", String(convention.providerCount))}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex rounded-md bg-card-muted px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {labels.types[convention.type] ?? convention.type}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground">
                    {convention.validityStart} {labels.validitySeparator} {convention.validityEnd}
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground">{formatBudget(convention.budget)}</td>
                  <td className="px-4 py-3">
                    <div className="flex min-w-[120px] items-center gap-2">
                      <span className="shrink-0 text-sm text-foreground">
                        {convention.consumptionPct.toFixed(2)}%
                      </span>
                      <div className="h-2 flex-1 overflow-hidden rounded-full bg-card-muted">
                        <div
                          className="h-full rounded-full bg-primary"
                          style={{ width: `${Math.min(convention.consumptionPct, 100)}%` }}
                        />
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={cn(
                        "inline-flex rounded-md px-2 py-1 text-[11px] font-semibold uppercase tracking-wide",
                        statusBadgeClass(convention.status),
                      )}
                    >
                      {labels.statuses[convention.status] ?? convention.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => navigate(`/dashboard/conventions/${convention.id}`)}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border text-primary transition-colors hover:bg-primary-soft"
                        aria-label="Voir"
                      >
                        <Eye size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => navigate(`/dashboard/conventions/${convention.id}/edit`)}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border text-yellow transition-colors hover:bg-yellow-soft"
                        aria-label="Modifier"
                      >
                        <Pencil size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={10} className="px-4 py-16 text-center text-sm text-muted">
                    {loading ? "Chargement…" : labels.empty}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
